import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Info, type LucideIcon } from "lucide-react";

import { orderDecisions, waitingTotal, type Decision } from "./deskLogic";

/** "Waiting for your decision": each queue a link to the list it counts, waiting ones first. */
export function DecisionsCard({ decisions, title = "Waiting for your decision", where = "across the school", aside }: { decisions: Decision[]; title?: string; where?: string; aside?: ReactNode }) {
  const rows = orderDecisions(decisions);
  const waiting = waitingTotal(decisions);
  return (
    <section className="card">
      <div className="card-titlebar">
        <div>
          <h2 className="font-semibold text-gray-900">{title}</h2>
          <p className="text-xs text-gray-500">{waiting ? `${waiting} ${waiting === 1 ? "item" : "items"} ${where}` : "Nothing is waiting for you right now."}</p>
        </div>
        {aside}
      </div>
      <ul className="grid divide-y divide-gray-100 md:grid-cols-2 md:divide-y-0">
        {rows.map((row) => (
          <li key={row.key} className="md:border-b md:border-gray-100 md:odd:border-r">
            <Link to={row.link} className={`group flex items-center gap-4 px-6 py-3.5 transition-colors hover:bg-gray-50 ${row.count ? "" : "opacity-70"}`}>
              <span className={`flex h-9 min-w-[2.25rem] shrink-0 items-center justify-center rounded-lg px-2 text-sm font-semibold tabular-nums ${row.count ? (row.urgent ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800") : "bg-gray-50 text-gray-400"}`}>
                {row.count || <CheckCircle2 className="h-4 w-4" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900">{row.label}</span>
                <span className="block truncate text-xs text-gray-500">{row.count ? row.note : "Nothing waiting"}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-primary-600" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface DeskTile { label: string; value: string; note: string; link: string; icon: LucideIcon; alert?: boolean }

/** Figure tiles, each opening the page behind it; amber when it needs attention. */
export function DeskTiles({ tiles }: { tiles: DeskTile[] }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {tiles.map((tile) => (
        <Link key={tile.label} to={tile.link} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}><tile.icon className="h-5 w-5" /></span>
          <span className="min-w-0">
            <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
            <span className={`block truncate text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
            <span className="block truncate text-xs text-gray-500" title={tile.note}>{tile.note}</span>
          </span>
        </Link>
      ))}
    </div>
  );
}

export function ViewerNote({ text }: { text: string }) {
  if (!text) return null;
  return (
    <div className="flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-900">
      <Info className="mt-0.5 h-4 w-4 shrink-0" /> <p>{text}</p>
    </div>
  );
}
